import type {
  ValidationError,
  ValidationErrorResponse,
} from '@/types/validation';

export class ApiValidationError extends Error {
  public readonly errors: ValidationError[];

  constructor(errors: ValidationError[], message = 'validation.generic') {
    super(message);
    this.name = 'ApiValidationError';
    this.errors = errors;
  }
}

export const isValidationErrorResponse = (
  value: unknown
): value is ValidationErrorResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    Array.isArray((value as ValidationErrorResponse).errors) &&
    (value as ValidationErrorResponse).errors.length > 0 &&
    typeof (value as ValidationErrorResponse).errors[0]?.message === 'string'
  );
};

export const extractValidationMessageKey = (
  value: unknown
): string | undefined => {
  if (!isValidationErrorResponse(value)) {
    return undefined;
  }

  return value.errors[0]?.message;
};

const normalizeErrorMessage = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value);

export const parseValidationErrors = (
  value: unknown
): ValidationError[] | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const rawErrors = (value as Record<string, unknown>).errors;

  if (!rawErrors) {
    return null;
  }

  if (Array.isArray(rawErrors)) {
    const mappedErrors = rawErrors
      .map(item => {
        if (typeof item === 'string') {
          return { message: item };
        }

        if (item && typeof item === 'object') {
          const typed = item as Partial<ValidationError>;
          if (typed.message) {
            return {
              field: typed.field ?? null,
              message: String(typed.message),
              parameters: typed.parameters ?? null,
            };
          }
        }
        return null;
      })
      .filter(Boolean) as ValidationError[];

    return mappedErrors.length > 0 ? mappedErrors : null;
  }

  if (typeof rawErrors === 'object') {
    const mappedErrors: ValidationError[] = [];
    Object.entries(rawErrors as Record<string, unknown>).forEach(
      ([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach(message =>
            mappedErrors.push({
              field,
              message: normalizeErrorMessage(message),
            })
          );
        } else if (messages) {
          mappedErrors.push({
            field,
            message: normalizeErrorMessage(messages),
          });
        }
      }
    );

    return mappedErrors.length > 0 ? mappedErrors : null;
  }

  return null;
};
